import { call, put, takeLatest } from 'redux-saga/effects';

import {
  CASE_FETCH_TAG_GROUPS_BEGIN,
  CASE_FETCH_TAG_GROUPS_SUCCESS,
  CASE_FETCH_TAG_GROUPS_FAILURE,
  CASE_FETCH_TAG_GROUPS_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const fetchTagGroups = createPromiseAction(CASE_FETCH_TAG_GROUPS_BEGIN);

export function dismissFetchTagGroupsFeedback() {
  return {
    type: CASE_FETCH_TAG_GROUPS_DISMISS_FEEDBACK,
  };
}

// worker Saga: will be fired on CASE_FETCH_TAG_GROUPS_BEGIN actions
export function* doFetchTagGroups(action) {
  const {
    payload: { caseId },
  } = action;

  const res = yield call(api.get, `/cases/${caseId}/tag-groups`);

  if (res && res.error) {
    yield put({
      type: CASE_FETCH_TAG_GROUPS_FAILURE,
      feedback: {
        message: 'feedback.fetchTagGroupsFailure',
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_FETCH_TAG_GROUPS_SUCCESS,
    data: res,
  });
  yield call(resolvePromiseAction, action, res);
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchFetchTagGroups() {
  yield takeLatest(fetchTagGroups, withCurrentCaseId(doFetchTagGroups));
}

export function useFetchTagGroups() {
  const dispatch = useDispatch();

  const { tagGroups, fetchTagGroupsPending, fetchTagGroupsFeedback } = useSelector(
    (state) => ({
      tagGroups: state.case.tagGroups,
      fetchTagGroupsPending: state.case.fetchTagGroupsPending,
      fetchTagGroupsFeedback: state.case.fetchTagGroupsFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(fetchTagGroups(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissFetchTagGroupsFeedback());
  }, [dispatch]);

  return {
    tagGroups,
    fetchTagGroups: boundAction,
    fetchTagGroupsPending,
    fetchTagGroupsFeedback,
    dismissFetchTagGroupsFeedback: boundDismissFeedback,
  };
}

// Redux reducer
export function reducer(state, action) {
  switch (action.type) {
    case CASE_FETCH_TAG_GROUPS_BEGIN + '.TRIGGER':
      return {
        ...state,
        fetchTagGroupsPending: true,
        fetchTagGroupsFeedback: null,
      };

    case CASE_FETCH_TAG_GROUPS_SUCCESS:
      return {
        ...state,
        fetchTagGroupsPending: false,
        fetchTagGroupsFeedback: action.feedback,
        tagGroups: action.data,
      };

    case CASE_FETCH_TAG_GROUPS_FAILURE:
      return {
        ...state,
        fetchTagGroupsPending: false,
        fetchTagGroupsFeedback: action.feedback,
      };

    case CASE_FETCH_TAG_GROUPS_DISMISS_FEEDBACK:
      return {
        ...state,
        fetchTagGroupsFeedback: null,
      };

    default:
      return state;
  }
}
